import React, { useState } from "react";
import {
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { EventTicketType } from "../../eventMockData";
import {
  FieldLabel,
  WizardActionButton,
  WizardStepProps,
  moveWizardRow,
  wizardCurrencyAdornmentClassName,
  wizardCurrencyInputClassName,
  wizardGreenCheckboxSx,
  wizardId,
  wizardTextFieldSx,
} from "./wizardShared";
import { ConfirmDialog } from "../../EventSales/eventSalesShared";
import {
  modelOffRedSwitchSx,
  switchSx,
} from "../../../MyChamber/ChamberSettings/common/switchStyles";
import CalendarDateInput from "../../../MyChamber/ChamberSettings/common/CalendarDateInput";
import { SectionShell } from "../../../../common/dashboard/SectionShell";
import { CurrencyInput } from "../../../../common/dashboard/CurrencyInput";

const TICKET_DESCRIPTION_MAX_LENGTH = 50;

// July 9, 2026 direction: new tickets start Members Only and visible on the
// registration form; the admin opts out of either (enable/disable toggle
// replaces the old forced Yes/No radios).
const blankTicket = (): EventTicketType => ({
  id: wizardId("ticket"),
  name: "",
  description: "",
  price: "",
  attendeesPerTicket: "1",
  memberOnly: true,
  code: "",
  availabilityLimit: "",
  salesStartDate: "",
  salesEndDate: "",
  visibleOnRegistrationForm: true,
});

// "May 1, 2026 - Jun 15, 2026" — short month, no leading zero, to match the
// CalendarDateInput display format used inside the ticket popup.
const salesDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

const salesPeriodLabel = (ticket: EventTicketType) => {
  if (ticket.salesStartDate || ticket.salesEndDate) {
    const formatDate = (value?: string) => {
      if (!value) {
        return "—";
      }
      const [year, month, day] = value.split("-").map(Number);
      if (!year || !month || !day) {
        return value;
      }
      return salesDateFormatter.format(new Date(year, month - 1, day));
    };
    return `${formatDate(ticket.salesStartDate)} - ${formatDate(ticket.salesEndDate)}`;
  }
  return "—";
};

const ticketTableHeadCellClassName =
  "!text-[12px] !font-bold !uppercase !tracking-wide !text-gray-600";

const ticketPopupDateInputClassName =
  "h-[52px] w-full rounded-md border border-gray-300 bg-white px-4 text-[16px] text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-cpaGold focus:ring-2 focus:ring-amber-100";

// Read-only $-display for the ticket table PRICE column: ticket.price is now
// the canonical decimal-dollars string ("85.00"), so the spec's "$85.00"
// column re-adds the "$" at render time (same shape as Step4's formatDollars).
const formatDollars = (value?: string): string => {
  const numeric = Number(String(value ?? "").replace(/[^0-9.]/g, ""));
  return `$${(Number.isFinite(numeric) ? numeric : 0).toFixed(2)}`;
};

const Step3TicketSetup = ({
  event,
  setEvent,
}: WizardStepProps): React.ReactElement => {
  const update = <K extends keyof typeof event>(key: K, value: (typeof event)[K]) =>
    setEvent((prev) => ({ ...prev, [key]: value }));

  const [draft, setDraft] = useState<EventTicketType | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<EventTicketType | null>(null);
  // Rows are reorderable via their six-dot drag handle (EVT-TICKET-001).
  const [ticketDragIndex, setTicketDragIndex] = useState<number | null>(null);
  const dropTicketRow = (targetIndex: number) => {
    if (ticketDragIndex === null || ticketDragIndex === targetIndex) {
      setTicketDragIndex(null);
      return;
    }
    update("tickets", moveWizardRow(event.tickets, ticketDragIndex, targetIndex));
    setTicketDragIndex(null);
  };

  const openAdd = () => {
    setDraft(blankTicket());
    setIsNew(true);
  };
  const openEdit = (ticket: EventTicketType) => {
    setDraft({ ...ticket });
    setIsNew(false);
  };
  const patchDraft = (patch: Partial<EventTicketType>) =>
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));

  const draftLimit = draft ? Number(draft.availabilityLimit) || 0 : 0;
  const setDraftLimit = (nextLimit: number) =>
    patchDraft({ availabilityLimit: nextLimit > 0 ? String(nextLimit) : "" });
  const draftAttendeesPerTicket = draft
    ? Math.max(1, Math.floor(Number(draft.attendeesPerTicket) || 1))
    : 1;

  const canSaveDraft = Boolean(
    draft &&
      (draft.code || "").trim() &&
      draft.name.trim() &&
      Number.isInteger(Number(draft.attendeesPerTicket || "1")) &&
      Number(draft.attendeesPerTicket || "1") >= 1
  );

  const saveDraft = () => {
    if (!draft || !canSaveDraft) {
      return;
    }
    // "For free tickets leave price set at $0.00" — an untouched price field
    // saves as $0.00 rather than an empty string. POL-005: prices are now the
    // canonical decimal-dollars shape ("0.00", no "$"); the "$" is re-added at
    // display time only.
    const ticket = {
      ...draft,
      price: draft.price.trim() || "0.00",
      attendeesPerTicket: String(draftAttendeesPerTicket),
    };
    update(
      "tickets",
      isNew
        ? [...event.tickets, ticket]
        : event.tickets.map((item) => (item.id === ticket.id ? ticket : item))
    );
    setDraft(null);
  };

  const confirmDelete = () => {
    if (!pendingDelete) {
      return;
    }
    update(
      "tickets",
      event.tickets.filter((item) => item.id !== pendingDelete.id)
    );
    setPendingDelete(null);
  };

  return (
    <>
      <SectionShell
        id="event-tickets"
        variant="pill"
        title="Tickets"
        description="Create ticket types and pricing options for your event."
      >
        <FormControlLabel
          sx={{ alignItems: "flex-start", mx: 0 }}
          control={
            <Checkbox
              checked={Boolean(event.eventIsFree)}
              onChange={({ target }) => update("eventIsFree", target.checked)}
              sx={{ pt: 0, ...wizardGreenCheckboxSx }}
            />
          }
          label={
            <div>
              <div className="text-[16px] font-extrabold leading-6 text-gray-950">
                This event is free (no tickets required)
              </div>
              <div className="text-[15px] leading-6 text-gray-500">
                Check this box if your event costs nothing to attend. Attendees simply register — no tickets or prices to set up.
              </div>
            </div>
          }
        />

        {!event.eventIsFree ? (
          <>
            <div className="mt-8 h-px w-[72%] bg-gray-200" />
            <div className="mt-8">
              <h3 className="text-[20px] font-extrabold leading-tight text-gray-950">
                Ticket Types
              </h3>
              <p className="mt-2 text-[16px] leading-6 text-gray-950">
                Edit or delete pre loaded ticket types below for attendees to choose from when registering for an event and select visibility. Or create your own ticket type.
              </p>
              <TableContainer className="mt-8 rounded-lg border border-gray-200">
                <Table>
                  <TableHead className="bg-gray-50">
                    <TableRow>
                      <TableCell className="!w-10" />
                      <TableCell className={ticketTableHeadCellClassName}>Code</TableCell>
                      <TableCell className={ticketTableHeadCellClassName}>Ticket Type</TableCell>
                      <TableCell className={ticketTableHeadCellClassName} align="center">
                        Price
                      </TableCell>
                      <TableCell className={ticketTableHeadCellClassName} align="center">
                        Attendees Included
                      </TableCell>
                      <TableCell className={ticketTableHeadCellClassName} align="center">
                        Availability Limit
                      </TableCell>
                      <TableCell className={ticketTableHeadCellClassName} align="center">
                        Sales Period
                      </TableCell>
                      <TableCell
                        className={`${ticketTableHeadCellClassName} !max-w-[170px]`}
                        align="center"
                      >
                        Visible on Registration Form
                      </TableCell>
                      <TableCell className={ticketTableHeadCellClassName}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {event.tickets.map((ticket, index) => (
                      <TableRow
                        key={ticket.id}
                        onDragOver={(dragEvent) => dragEvent.preventDefault()}
                        onDrop={() => dropTicketRow(index)}
                        className={ticketDragIndex === index ? "bg-gray-50" : ""}
                      >
                        <TableCell>
                          <span
                            draggable
                            onDragStart={() => setTicketDragIndex(index)}
                            onDragEnd={() => setTicketDragIndex(null)}
                            className="cursor-grab"
                          >
                            <DragIndicatorIcon sx={{ color: "#111827", fontSize: 16 }} />
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="font-bold text-gray-950">{ticket.code || "—"}</span>
                        </TableCell>
                        <TableCell>
                          <div className="text-[16px] font-extrabold text-gray-950">
                            {ticket.name}
                          </div>
                          {ticket.description ? (
                            <div className="max-w-[330px] text-[14px] leading-5 text-gray-700">
                              {ticket.description}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell align="center">
                          {/* POL-005: prices store canonically ("85.00"); the
                              spec's "$85.00" column display re-adds the "$". */}
                          <span className="font-extrabold text-black">
                            {formatDollars(ticket.price)}
                          </span>
                        </TableCell>
                        <TableCell align="center">
                          <span className="font-extrabold text-black">
                            {Math.max(
                              1,
                              Math.floor(Number(ticket.attendeesPerTicket) || 1)
                            )}
                          </span>
                        </TableCell>
                        <TableCell align="center">
                          {ticket.availabilityLimit || "No limit"}
                        </TableCell>
                        <TableCell align="center" className="!max-w-[150px]">
                          {salesPeriodLabel(ticket)}
                        </TableCell>
                        <TableCell align="center">
                          <span
                            className={`text-[13px] font-extrabold uppercase ${
                              ticket.visibleOnRegistrationForm === false
                                ? "text-red-500"
                                : "text-emerald-500"
                            }`}
                          >
                            {ticket.visibleOnRegistrationForm === false ? "NO" : "YES"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <WizardActionButton
                              tone="gold"
                              size="small"
                              startIcon={<EditOutlinedIcon />}
                              onClick={() => openEdit(ticket)}
                            >
                              Edit
                            </WizardActionButton>
                            <WizardActionButton
                              tone="red"
                              size="small"
                              startIcon={<DeleteOutlineIcon />}
                              onClick={() => setPendingDelete(ticket)}
                            >
                              Delete
                            </WizardActionButton>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <WizardActionButton
                className="!mt-6"
                tone="gold"
                startIcon={<AddIcon />}
                onClick={openAdd}
              >
                Add Ticket Type
              </WizardActionButton>
            </div>
          </>
        ) : null}
      </SectionShell>

      <SectionShell
        id="event-capacity"
        variant="pill"
        title="Event Capacity"
        description="Set the total maximum number of attendees for this event."
      >
        <div className="flex items-center gap-3">
          <TextField
            size="small"
            type="number"
            value={event.capacity}
            onChange={({ target }) => update("capacity", target.value)}
            sx={{ ...wizardTextFieldSx, maxWidth: 220 }}
          />
          <span className="text-[16px] text-gray-950">attendees</span>
        </div>

        <div className="mt-8 rounded-xl border border-cpaGold px-8 py-7">
          <h4 className="text-[20px] font-extrabold leading-tight text-gray-950">
            Ticket Settings
          </h4>
          <p className="mt-1 text-[16px] leading-6 text-gray-500">
            Select the options below that you would like to apply.
          </p>
          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
            <FormControlLabel
              sx={{ alignItems: "flex-start", mx: 0 }}
              control={
                <Checkbox
                  checked={event.displayRemainingTicketQuantity ?? true}
                  onChange={({ target }) =>
                    update("displayRemainingTicketQuantity", target.checked)
                  }
                  // Event-wizard checkboxes are green (July 9, 2026 direction).
                  sx={{ pt: 0, ...wizardGreenCheckboxSx }}
                />
              }
              label={
                <div>
                  <div className="text-[16px] font-extrabold leading-6 text-gray-950">
                    Display remaining ticket quantity
                  </div>
                  <div className="text-[15px] leading-6 text-gray-500">
                    Show total of how many tickets are left
                  </div>
                </div>
              }
            />
            <FormControlLabel
              sx={{ alignItems: "flex-start", mx: 0 }}
              control={
                <Checkbox
                  checked={Boolean(event.displayRemainingTicketTypeQuantity)}
                  onChange={({ target }) =>
                    update("displayRemainingTicketTypeQuantity", target.checked)
                  }
                  sx={{ pt: 0, ...wizardGreenCheckboxSx }}
                />
              }
              label={
                <div>
                  <div className="text-[16px] font-extrabold leading-6 text-gray-950">
                    Display remaining ticket type quantity
                  </div>
                  <div className="text-[15px] leading-6 text-gray-500">
                    Show how many tickets are left for each type.
                  </div>
                </div>
              }
            />
            <FormControlLabel
              sx={{ alignItems: "flex-start", mx: 0 }}
              control={
                <Checkbox
                  checked={Boolean(event.allowWaitlist)}
                  onChange={({ target }) => update("allowWaitlist", target.checked)}
                  sx={{ pt: 0, ...wizardGreenCheckboxSx }}
                />
              }
              label={
                <div>
                  <div className="text-[16px] font-extrabold leading-6 text-gray-950">
                    Allow wait list when sold out
                  </div>
                  <div className="text-[15px] leading-6 text-gray-500">
                    Attendees can join a wait list if tickets sell out.
                  </div>
                </div>
              }
            />
          </div>
        </div>
      </SectionShell>

      {/* Add / Edit ticket dialog */}
      <Dialog open={Boolean(draft)} onClose={() => setDraft(null)} maxWidth="md" fullWidth>
        <DialogContent className="relative !px-8 !py-8">
          <IconButton
            size="small"
            onClick={() => setDraft(null)}
            className="!absolute right-2 top-2 text-gray-400"
            aria-label="close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <h3 className="text-[22px] font-extrabold leading-tight text-gray-950">
            {isNew ? "Add Ticket Type" : "Edit Ticket Type"}
          </h3>
          <p className="mt-1 text-[16px] leading-6 text-gray-950">
            {isNew
              ? "Add the details for this ticket type."
              : "Edit the details for this ticket type."}
          </p>
          {draft ? (
            <div className="mt-8 space-y-8">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="flex flex-col justify-end">
                  <FieldLabel required>Ticket Type Code</FieldLabel>
                  <TextField
                    fullWidth
                    placeholder="Enter Ticket Type Code"
                    value={draft.code || ""}
                    onChange={({ target }) => patchDraft({ code: target.value })}
                    sx={wizardTextFieldSx}
                  />
                </div>
                <div className="flex flex-col justify-end">
                  <FieldLabel required hint="Appears on registration form for attendees to select.">
                    Ticket Type Name
                  </FieldLabel>
                  <TextField
                    fullWidth
                    placeholder="Enter Ticket Name"
                    value={draft.name}
                    onChange={({ target }) => patchDraft({ name: target.value })}
                    sx={wizardTextFieldSx}
                  />
                </div>
              </div>
              <div>
                <FieldLabel hint="Optional brief description to appear below ticket type.">
                  Ticket Type Description <span className="font-normal">(Optional)</span>
                </FieldLabel>
                <TextField
                  fullWidth
                  placeholder="Enter Ticket Type Description"
                  value={draft.description}
                  onChange={({ target }) =>
                    patchDraft({
                      description: target.value.slice(0, TICKET_DESCRIPTION_MAX_LENGTH),
                    })
                  }
                  sx={wizardTextFieldSx}
                />
                <p className="mt-1 text-[14px] text-gray-400">
                  {draft.description.length}/{TICKET_DESCRIPTION_MAX_LENGTH} characters
                </p>
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="flex flex-col justify-end">
                  <FieldLabel
                    hint={
                      isNew
                        ? "Set the price attendees will pay for this ticket."
                        : "Set the price attendees will pay for this ticket. For free tickets leave price set at $0.00"
                    }
                  >
                    Price
                  </FieldLabel>
                  {/* POL-005: shared CurrencyInput ($ adornment + "0.00"
                      placeholder converge on the mock's "$0.00" — same
                      sanctioned convergence B5 applied to invoice unit price);
                      wizard-skin overrides keep the 58px MUI-outline look. */}
                  <CurrencyInput
                    ariaLabel="Price"
                    placeholder="0.00"
                    value={draft.price}
                    onChange={(value) => patchDraft({ price: value })}
                    inputClassName={wizardCurrencyInputClassName}
                    adornmentClassName={wizardCurrencyAdornmentClassName}
                  />
                </div>
                <div className="flex flex-col justify-end">
                  <FieldLabel hint="Choose the date range when this ticket type can be sold. Allow presale of tickets before opening up to public.">
                    Sales Period <span className="font-normal">(Optional)</span>
                  </FieldLabel>
                  <div className="grid grid-cols-2 gap-4">
                    <CalendarDateInput
                      mode="fullDate"
                      ariaLabel="Sales period start date"
                      placeholder="Start Date"
                      inputClassName={ticketPopupDateInputClassName}
                      value={draft.salesStartDate || ""}
                      onChange={(value) => patchDraft({ salesStartDate: value })}
                    />
                    <CalendarDateInput
                      mode="fullDate"
                      ariaLabel="Sales period end date"
                      placeholder="End Date"
                      inputClassName={ticketPopupDateInputClassName}
                      value={draft.salesEndDate || ""}
                      onChange={(value) => patchDraft({ salesEndDate: value })}
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <FieldLabel hint="Set how many attendee registrations are included when one unit of this ticket is purchased.">
                    Attendees Included Per Ticket
                  </FieldLabel>
                  <TextField
                    fullWidth
                    type="number"
                    value={draft.attendeesPerTicket || "1"}
                    onChange={({ target }) =>
                      patchDraft({ attendeesPerTicket: target.value })
                    }
                    inputProps={{ min: 1, step: 1 }}
                    sx={wizardTextFieldSx}
                  />
                  <p className="mt-1 text-[14px] leading-5 text-gray-500">
                    Use 1 for an individual ticket or 8 for a table of eight.
                  </p>
                </div>
                <div>
                  <FieldLabel hint="Set if you want to limit # of ticket packages available.">
                    Availability Limit <span className="font-normal">(Optional)</span>
                  </FieldLabel>
                  <div className="flex h-[52px] w-[170px] items-center justify-between rounded-lg border border-gray-300 px-5">
                    <button
                      type="button"
                      aria-label="Decrease availability limit"
                      className="text-[18px] text-gray-500"
                      onClick={() => setDraftLimit(Math.max(0, draftLimit - 1))}
                    >
                      -
                    </button>
                    <span className="text-[17px] text-gray-950">{draftLimit}</span>
                    <button
                      type="button"
                      aria-label="Increase availability limit"
                      className="text-[18px] text-gray-500"
                      onClick={() => setDraftLimit(draftLimit + 1)}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-[19px] font-extrabold leading-tight text-gray-950">
                  Visible on Registration Form
                </h4>
                <p className="mt-1 text-[15px] leading-6 text-gray-950">
                  Choose whether this ticket type should be visible on the online registration for attendees to select or for internal use only.
                </p>
                {/* July 9, 2026 direction: the Yes/No radios are amalgamated
                    into the Chamber Setup membership-model enable/disable
                    toggle — enabled by default, red track when disabled. */}
                <div className="mt-4 flex items-center gap-3">
                  <Switch
                    checked={draft.visibleOnRegistrationForm ?? true}
                    onChange={({ target }) =>
                      patchDraft({ visibleOnRegistrationForm: target.checked })
                    }
                    inputProps={{ "aria-label": "Visible on Registration Form" }}
                    sx={
                      (draft.visibleOnRegistrationForm ?? true)
                        ? switchSx
                        : modelOffRedSwitchSx
                    }
                  />
                  <span
                    className={`text-xs font-bold uppercase ${
                      (draft.visibleOnRegistrationForm ?? true)
                        ? "text-emerald-600"
                        : "text-red-500"
                    }`}
                  >
                    {(draft.visibleOnRegistrationForm ?? true)
                      ? "Enabled"
                      : "Disabled"}
                  </span>
                </div>
                <p className="mt-2 text-[14px] leading-6 text-gray-700">
                  {(draft.visibleOnRegistrationForm ?? true)
                    ? "Attendees will be able to select and purchase this ticket."
                    : "This ticket will not be visible to attendees. Internal Use Only"}
                </p>
                {/* Q-007: Members Only stays available below the visibility
                    toggle. Per the confirmed meaning (Q-000f) a Members Only
                    ticket remains publicly visible; member selection is
                    required at registration. New tickets start checked
                    (July 9, 2026 direction) — untick for non-member tickets. */}
                <FormControlLabel
                  sx={{ alignItems: "flex-start", mx: 0, mt: 2.5 }}
                  control={
                    <Checkbox
                      checked={draft.memberOnly}
                      onChange={({ target }) => patchDraft({ memberOnly: target.checked })}
                      sx={{ pt: 0, ...wizardGreenCheckboxSx }}
                    />
                  }
                  label={
                    <div>
                      <div className="text-[17px] font-extrabold leading-6 text-gray-950">
                        Members Only
                      </div>
                      <div className="mt-1 text-[15px] leading-6 text-gray-700">
                        This ticket is visible to everyone on the website, but registering requires selecting a member from the member list.
                      </div>
                    </div>
                  }
                />
              </div>
              <div className="pt-2">
                <WizardActionButton tone="gold" disabled={!canSaveDraft} onClick={saveDraft}>
                  {isNew ? "Add Ticket Type" : "Save Ticket Type"}
                </WizardActionButton>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        tone="danger"
        title="Are you sure you want to delete this ticket type?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
};

export default Step3TicketSetup;
